import React, { useCallback, useMemo, useRef, useState } from 'react'
import { Button, Modal } from 'antd'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import { useSelector } from 'react-redux'
import { ColDef, ColGroupDef, CellValueChangedEvent, CellSelectionOptions } from 'ag-grid-enterprise'
import { AgGridReact } from 'ag-grid-react'
import { createDate } from '../../../config/dayjsConfig'
import { formatValueByType } from '../../../utils/helpers'
import { VariableType } from '../../../components/Table/types'
import { NumericCellEditor } from '../../../shared/components/custom/Grid/Editor'
import CustomAgGrid from '../../../shared/components/custom/Grid/CustomAgGrid'
import { filtersSelector } from '../../../redux/context/filters/selectors'
import { useDepreciations } from '../hooks/useDepreciations'
import { useBatchSaveDepreciationCorrections } from '../hooks/useBatchSaveCorrections'
import { DeprecationSeriesReportRow, DepreciationCorrectionPayload } from '../types'

interface DepreciationModalProps {
  investmentId: number
  handleOk: () => void
  visible: boolean
}

type PlanKey = 'deprecationPlan' | 'taxDeprecationPlan'

const formatAbsolute = (value: number | undefined | null) =>
  formatValueByType(value ?? undefined, VariableType.absolute, {
    maximumFractionDigits: 2,
    minimumFractionDigits: 2
  })

const InvestmentDeprecationModal: React.FC<DepreciationModalProps> = ({ investmentId, visible, handleOk }) => {
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  const { budgetingScenario } = useSelector(filtersSelector)
  const { data: depreciationData, isLoading, isFetching } = useDepreciations(investmentId)
  const batchSaveMutation = useBatchSaveDepreciationCorrections()

  const gridRef = useRef<AgGridReact>(null)
  const [isDirty, setIsDirty] = useState(false)

  /** Clone react-query data so AG Grid valueSetter doesn't mutate the cache */
  const rowData = useMemo(() => (depreciationData ? structuredClone(depreciationData) : undefined), [depreciationData])

  const isLocked = budgetingScenario?.isLocked === true
  const hasTaxDeprecation = depreciationData?.[0]?.taxDeprecationPlan != null

  /** Compare current grid value against original react-query data */
  const isCellDirty = useCallback(
    (data: DeprecationSeriesReportRow, planKey: PlanKey) => {
      if (!depreciationData) return false
      const original = depreciationData.find(r => r.year === data.year && r.month === data.month)
      const currentValue = data[planKey]?.correction?.value ?? 0
      const originalValue = original?.[planKey]?.correction?.value ?? 0
      return currentValue !== originalValue
    },
    [depreciationData]
  )

  /** Check if any row in the grid has changes vs original data */
  const checkDirty = useCallback(() => {
    const api = gridRef.current?.api
    if (!api || !depreciationData) {
      setIsDirty(false)
      return
    }

    let dirty = false
    api.forEachNode(node => {
      if (dirty || !node.data) return
      if (isCellDirty(node.data, 'deprecationPlan')) dirty = true
      if (hasTaxDeprecation && isCellDirty(node.data, 'taxDeprecationPlan')) dirty = true
    })
    setIsDirty(dirty)
  }, [depreciationData, isCellDirty, hasTaxDeprecation])

  /** Build corrections payload by diffing grid data against original */
  const buildCorrections = useCallback((): DepreciationCorrectionPayload[] => {
    const api = gridRef.current?.api
    if (!api || !depreciationData) return []

    const corrections: DepreciationCorrectionPayload[] = []

    const addIfDirty = (row: DeprecationSeriesReportRow, planKey: PlanKey) => {
      if (!isCellDirty(row, planKey)) return

      const original = depreciationData.find(r => r.year === row.year && r.month === row.month)
      const currentValue = row[planKey]?.correction?.value ?? 0
      const id = original?.[planKey]?.correction?.id
      const deprecationPlanId = row[planKey]?.id
      if (deprecationPlanId == null) return

      corrections.push({
        deprecationPlanId,
        year: row.year,
        month: row.month,
        value: currentValue === 0 && id ? null : currentValue,
        ...(id && { id })
      })
    }

    api.forEachNode(node => {
      if (!node.data) return
      addIfDirty(node.data, 'deprecationPlan')
      if (hasTaxDeprecation) addIfDirty(node.data, 'taxDeprecationPlan')
    })

    return corrections
  }, [depreciationData, isCellDirty, hasTaxDeprecation])

  const onCellValueChanged = useCallback(
    (event: CellValueChangedEvent<DeprecationSeriesReportRow>) => {
      checkDirty()
      event.api.refreshCells({ force: true })
    },
    [checkDirty]
  )

  const correctionValueSetter = useCallback(
    (params: { data: DeprecationSeriesReportRow; newValue: number | null; colDef: ColDef }) => {
      const planKey = params.colDef.colId as PlanKey
      const plan = params.data[planKey]
      if (!plan) return false

      const value = params.newValue ?? 0
      if (!plan.correction) {
        plan.correction = { id: 0, value }
      } else {
        plan.correction = { ...plan.correction, value }
      }
      return true
    },
    []
  )

  const dirtyCellClassRules = useCallback(
    (planKey: PlanKey) => ({
      'ag-cell--dirty': (params: { data?: DeprecationSeriesReportRow }) =>
        !!params.data && isCellDirty(params.data, planKey)
    }),
    [isCellDirty]
  )

  const columnDefs = useMemo((): (ColDef<DeprecationSeriesReportRow> | ColGroupDef<DeprecationSeriesReportRow>)[] => {
    const dateCol: ColDef<DeprecationSeriesReportRow> = {
      headerName: t('investmentsPage:depreciationDate'),
      valueGetter: ({ data }) => {
        if (!data) return ''
        const date = createDate(data.year, data.month)
        return date ? date.endOf('month').format('DD.MM.YYYY') : ''
      },
      flex: 1,
      sortable: false
    }

    const accountingGroup: ColGroupDef<DeprecationSeriesReportRow> = {
      headerName: t('investmentsPage:accounting'),
      children: [
        {
          headerName: t('investmentsPage:deprecation'),
          field: 'deprecationPlan.deprecation',
          valueFormatter: ({ value }) => formatAbsolute(value),
          type: 'numericColumn',
          flex: 1,
          sortable: false
        },
        {
          headerName: t('investmentsPage:correction'),
          colId: 'deprecationPlan',
          field: 'deprecationPlan.correction.value',
          valueFormatter: ({ value }) => formatAbsolute(value),
          editable: () => !isLocked,
          cellEditor: NumericCellEditor,
          valueSetter: correctionValueSetter,
          cellClassRules: dirtyCellClassRules('deprecationPlan'),
          type: 'numericColumn',
          flex: 1,
          sortable: false
        },
        {
          headerName: t('investmentsPage:taxDeprecation'),
          field: 'deprecationPlan.residual',
          valueFormatter: ({ value }) => formatAbsolute(value),
          type: 'numericColumn',
          flex: 1,
          sortable: false
        }
      ]
    }

    const taxGroup: ColGroupDef<DeprecationSeriesReportRow> = {
      headerName: t('investmentsPage:taxation'),
      children: [
        {
          headerName: t('investmentsPage:deprecation'),
          field: 'taxDeprecationPlan.deprecation',
          valueFormatter: ({ value }) => formatAbsolute(value),
          type: 'numericColumn',
          flex: 1,
          sortable: false
        },
        {
          headerName: t('investmentsPage:correction'),
          colId: 'taxDeprecationPlan',
          field: 'taxDeprecationPlan.correction.value',
          valueFormatter: ({ value }) => formatAbsolute(value),
          editable: () => !isLocked,
          cellEditor: NumericCellEditor,
          valueSetter: correctionValueSetter,
          cellClassRules: dirtyCellClassRules('taxDeprecationPlan'),
          type: 'numericColumn',
          flex: 1,
          sortable: false
        },
        {
          headerName: t('investmentsPage:taxDeprecation'),
          field: 'taxDeprecationPlan.residual',
          valueFormatter: ({ value }) => formatAbsolute(value),
          type: 'numericColumn',
          flex: 1,
          sortable: false
        }
      ]
    }

    const acceleratedGroup: ColGroupDef<DeprecationSeriesReportRow> = {
      headerName: t('investmentsPage:acceleratedDeprecation'),
      children: [
        {
          headerName: t('investmentsPage:change'),
          field: 'acceleratedDeprecations.delta',
          valueFormatter: ({ value }) => formatAbsolute(value),
          type: 'numericColumn',
          flex: 1,
          sortable: false
        },
        {
          headerName: t('investmentsPage:acceleratedDeprecation'),
          field: 'acceleratedDeprecations.deprecation',
          valueFormatter: ({ value }) => formatAbsolute(value),
          type: 'numericColumn',
          flex: 1,
          sortable: false
        }
      ]
    }

    const cols: (ColDef<DeprecationSeriesReportRow> | ColGroupDef<DeprecationSeriesReportRow>)[] = [
      dateCol,
      accountingGroup
    ]

    if (hasTaxDeprecation) {
      cols.push(taxGroup)
      cols.push(acceleratedGroup)
    }

    return cols
  }, [t, isLocked, hasTaxDeprecation, dirtyCellClassRules, correctionValueSetter])

  const defaultColDef = useMemo(
    (): ColDef => ({
      suppressMovable: true,
      editable: false
    }),
    []
  )

  const cellSelection = useMemo(
    (): CellSelectionOptions => ({
      handle: {
        mode: 'fill' as const,
        direction: 'y'
      }
    }),
    []
  )

  const handleSave = useCallback(() => {
    const corrections = buildCorrections()
    if (corrections.length === 0) return

    batchSaveMutation.mutate(
      { investmentId, corrections },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: [{ scope: 'investment' }] })
          setIsDirty(false)
        }
      }
    )
  }, [investmentId, buildCorrections, batchSaveMutation, queryClient])

  const handleCancel = useCallback(() => {
    if (depreciationData && gridRef.current?.api) {
      gridRef.current.api.setGridOption('rowData', structuredClone(depreciationData))
    }
    setIsDirty(false)
    handleOk()
  }, [handleOk, depreciationData])

  const getRowId = useCallback(({ data }: { data: DeprecationSeriesReportRow }) => `${data.year}-${data.month}`, [])

  return (
    <Modal
      open={visible}
      style={{ maxWidth: '1500px' }}
      title={t('investmentsPage:depreciations')}
      onOk={handleCancel}
      onCancel={handleCancel}
      maskClosable={false}
      data-cy="depreciation-modal"
      footer={
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Button data-cy="depreciation-modal-cancel" onClick={handleCancel}>
            {t('global:cancel')}
          </Button>
          {!isLocked && (
            <Button
              data-cy="depreciation-modal-save"
              type="primary"
              onClick={handleSave}
              disabled={!isDirty}
              loading={batchSaveMutation.isPending}
            >
              {t('global:save')}
            </Button>
          )}
        </div>
      }
      width="unset"
    >
      <div data-cy="depreciation-modal-grid" style={{ height: 700 }}>
        <CustomAgGrid
          ref={gridRef}
          rowData={rowData}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          getRowId={getRowId}
          onCellValueChanged={onCellValueChanged}
          loading={isLoading || isFetching}
          cellSelection={cellSelection}
          undoRedoCellEditing
          singleClickEdit={false}
          stopEditingWhenCellsLoseFocus
        />
      </div>
    </Modal>
  )
}

export default InvestmentDeprecationModal
